import createVideo from '@helpers/dom/createVideo';
import renderImageFromUrl from '@helpers/dom/renderImageFromUrl';
import {Document, DocumentAttribute, StickerSet} from '@layer';
import appDownloadManager from '@lib/appDownloadManager';
import {AppManagers} from '@lib/managers';
import lottieLoader from '@lib/lottie/lottieLoader';
import rootScope from '@lib/rootScope';
import animationIntersector, {AnimationItemGroup} from '@components/animationIntersector';
import LazyLoadQueue from '@components/lazyLoadQueue';
import wrapSticker from '@components/wrappers/sticker';
import {Middleware} from '@helpers/middleware';
import {EMOJI_TEXT_COLOR} from '@components/emoticonsDropdown';
import {getStickerSetInputById} from '@lib/appManagers/utils/stickers/getStickerSetInput';
import {createObjectURL, revokeObjectURL} from '@helpers/objectUrl';
import clearMediaElementSource from '@helpers/dom/clearMediaElementSource';

export default async function wrapStickerSetThumb({set, lazyLoadQueue, container, group, autoplay, width, height, managers = rootScope.managers, middleware, textColor}: {
  set: StickerSet.stickerSet,
  lazyLoadQueue: LazyLoadQueue,
  container: HTMLElement,
  group: AnimationItemGroup,
  autoplay: boolean,
  width: number,
  height: number,
  managers?: AppManagers
  middleware: Middleware,
  textColor?: WrapSomethingOptions['textColor']
}) {
  if(set.thumbs?.length) {
    container.classList.add('media-sticker-wrapper');
    lazyLoadQueue.push({
      div: container,
      middleware,
      load: async() => {
        const downloadOptions = await managers.appStickersManager.getStickerSetThumbDownloadOptions(set);
        const promise = appDownloadManager.download(downloadOptions);

        const isLottie = downloadOptions.mimeType === 'application/x-tgsticker';
        if(isLottie) {
          return promise.then((blob) => {
            lottieLoader.loadAnimationWorker({
              container,
              loop: true,
              autoplay,
              animationData: blob,
              width,
              height,
              needUpscale: true,
              name: 'setThumb' + set.id,
              group,
              middleware
            });
          });
        } else {
          const isVideo = set.thumbs?.some((thumb) => thumb.type === 'v');
          let media: HTMLElement;
          if(isVideo) {
            media = createVideo({middleware});
            (media as HTMLVideoElement).autoplay = true;
            (media as HTMLVideoElement).muted = true;
            (media as HTMLVideoElement).loop = true;
          } else {
            media = new Image();
          }

          media.classList.add('media-sticker');

          return promise.then((blob) => {
            if(!middleware()) {
              return;
            }

            const url = createObjectURL(blob);
            middleware.onClean(() => {
              clearMediaElementSource(media as HTMLImageElement | HTMLMediaElement);
              revokeObjectURL(url);
            });
            renderImageFromUrl(media, url, () => {
              if(!middleware()) {
                return;
              }

              container.append(media);

              if(isVideo) {
                animationIntersector.addAnimation({
                  animation: media as HTMLVideoElement,
                  controlled: middleware,
                  group,
                  observeElement: media,
                  type: 'video'
                });
              }
            });
          });
        }
      }
    });

    return;
  }

  let getDocPromise: Promise<Document.document>;

  if(set.thumb_document_id) {
    getDocPromise = managers.appEmojiManager.getCustomEmojiDocument(set.thumb_document_id);
  } else {
    getDocPromise = managers.appStickersManager.getStickerSet(getStickerSetInputById(set)).then((stickerSet) => stickerSet.documents[0] as Document.document);
  }

  const doc = await getDocPromise;
  if(!doc) {
    return;
  }

  const attribute = doc.attributes.find((a) => a._ === 'documentAttributeCustomEmoji') as DocumentAttribute.documentAttributeCustomEmoji
  // as thumb will be used first sticker
  wrapSticker({
    doc,
    div: container,
    group: group,
    lazyLoadQueue,
    managers,
    width,
    height,
    middleware,
    textColor: attribute?.pFlags?.text_color ? textColor || EMOJI_TEXT_COLOR : undefined
  }); // kostil
}
